import type { MaybeElementRef } from "@vueuse/core";
import { unrefElement, useEventListener } from "@vueuse/core";
import { isString } from "@/common/general";

interface UseEventOutsideOptions {
  ignore?: (MaybeElementRef | string)[];
}

function createFilteringListener(
  target: MaybeElementRef,
  handler: (event: PointerEvent) => void,
  ignore: Exclude<UseEventOutsideOptions["ignore"], undefined>,
) {
  const shouldIgnore = (event: PointerEvent) => ignore.some(ignoreTarget => {
    if (isString(ignoreTarget)) {
      return Array.from(window.document.querySelectorAll(ignoreTarget))
        .some(el => el === event.target || event.composedPath().includes(el));
    }
    const el = unrefElement(ignoreTarget);
    return el && (event.target === el || event.composedPath().includes(el));
  });

  const listener = (event: PointerEvent) => {
    if (shouldIgnore(event)) return;

    const el = unrefElement(target);
    if (!el || el === event.target || event.composedPath().includes(el)) return;

    handler(event);
  };

  return listener;
}

export function useEventOutside(
  target: MaybeElementRef,
  event: string,
  handler: (event: PointerEvent) => void,
  options: UseEventOutsideOptions = {},
) {
  const { ignore = [] } = options;
  if (!window) return;

  const listener = createFilteringListener(target, handler, ignore);
  useEventListener(window, event, listener);
}
